import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import api from 'api';
import { setAlert } from 'redux/alert';
import type { ClientCompanyDocumentTemplate, DocumentInterface } from 'types';

/** Documents page and the add-document dialog remount this data for one organization. */
export const ORGANIZATION_DOCUMENT_STALE_MS = 5 * 60 * 1000;

export const organizationDocumentKeys = {
    all: ['organizationDocuments'] as const,
    list: (serviceProviderId: string) =>
        ['organizationDocuments', 'list', serviceProviderId] as const,
    templates: (serviceProviderId: string) =>
        ['organizationDocuments', 'templates', serviceProviderId] as const,
};

export const organizationDocumentListQuery = (serviceProviderId: string) => ({
    queryKey: organizationDocumentKeys.list(serviceProviderId),
    queryFn: async (): Promise<DocumentInterface[]> => {
        const response =
            await api.clientServiceProviders.getClientCompanyDocuments(serviceProviderId);
        return response.data ?? [];
    },
    staleTime: ORGANIZATION_DOCUMENT_STALE_MS,
    retry: false,
});

export const organizationDocumentTemplateQuery = (serviceProviderId: string) => ({
    queryKey: organizationDocumentKeys.templates(serviceProviderId),
    queryFn: async (): Promise<ClientCompanyDocumentTemplate[]> => {
        const response =
            await api.documentTemplates.getClientCompanyDocumentTemplates(serviceProviderId);
        return response.data ?? [];
    },
    staleTime: ORGANIZATION_DOCUMENT_STALE_MS,
    retry: false,
});

/** One toast per failed fetch when a screen remounts onto a cached error. */
let lastReportedDocumentListErrorAt = 0;
let lastReportedDocumentTemplateErrorAt = 0;

export interface UseOrganizationDocumentsOptions {
    enabled?: boolean;
    alertOnError?: boolean;
}

/**
 * GET /client-service-providers/:id/documents
 * Cached per organization so leaving and returning to Documents does not refetch.
 */
export const useOrganizationDocuments = (
    serviceProviderId?: string | null,
    options?: UseOrganizationDocumentsOptions
) => {
    const dispatch = useDispatch();
    const id = serviceProviderId ?? '';
    const alertOnError = options?.alertOnError ?? false;
    const query = useQuery({
        ...organizationDocumentListQuery(id),
        enabled: (options?.enabled ?? true) && Boolean(id),
    });
    const { errorUpdatedAt, isError, isFetching } = query;

    useEffect(() => {
        if (!alertOnError || !isError || isFetching || errorUpdatedAt === 0) {
            return;
        }
        if (lastReportedDocumentListErrorAt === errorUpdatedAt) {
            return;
        }
        lastReportedDocumentListErrorAt = errorUpdatedAt;
        dispatch(setAlert({ message: 'somethingWentWrong', type: 'error' }));
    }, [alertOnError, dispatch, errorUpdatedAt, isError, isFetching]);

    return query;
};

/**
 * GET /document-templates for one organization.
 * The add-document dialog remounts this list each time it opens.
 */
export const useOrganizationDocumentTemplates = (
    serviceProviderId?: string | null,
    options?: UseOrganizationDocumentsOptions
) => {
    const dispatch = useDispatch();
    const id = serviceProviderId ?? '';
    const alertOnError = options?.alertOnError ?? false;
    const query = useQuery({
        ...organizationDocumentTemplateQuery(id),
        enabled: (options?.enabled ?? true) && Boolean(id),
    });
    const { errorUpdatedAt, isError, isFetching } = query;

    useEffect(() => {
        if (!alertOnError || !isError || isFetching || errorUpdatedAt === 0) {
            return;
        }
        if (lastReportedDocumentTemplateErrorAt === errorUpdatedAt) {
            return;
        }
        lastReportedDocumentTemplateErrorAt = errorUpdatedAt;
        dispatch(setAlert({ message: 'somethingWentWrong', type: 'error' }));
    }, [alertOnError, dispatch, errorUpdatedAt, isError, isFetching]);

    return query;
};
